/**
 * MediaGrid — foto/video-grid uit de eigen media-laag (fase 2, CompanyCam-exit).
 *
 * Herbruikbaar op item-detail (wiSfId) en WO-detail (woSfId). Klik op een
 * foto opent de MediaLightbox (zelfde UX als de klant-timeline); video's
 * spelen inline af.
 *
 * Corrigeren: kantoor kan per thumbnail verwijderen (hover-prullenbak) of
 * meerdere selecteren en verwijderen/naar een andere fase verplaatsen
 * (POST /api/media/corrigeren). Dat is nodig omdat een inspecteur foto's op
 * het verkeerde kleed of met de verkeerde fase-knop kan schieten, en de
 * klant anders naar het verkeerde kleed blijft kijken.
 * Verplaatsen naar een ánder WI kan het endpoint wel, maar staat bewust niet
 * in deze UI: dat vraagt een kleed-zoeker en gaat via het Brein of een script.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  corrigeerMedia,
  fetchMedia,
  klantFotoNummers,
  MEDIA_FASE_LABELS,
  type MediaItem,
} from '../lib/media';
import { MediaLightbox, type LightboxFoto } from './MediaLightbox';

interface Props {
  wiSfId?: string;
  woSfId?: string;
  /** Niets renderen als er geen eigen media is (voor optionele secties). */
  hideWhenEmpty?: boolean;
  /** Render als zelfstandige kaart met deze titel (verbergt zichzelf bij leeg). */
  cardTitle?: string;
  /** Selecteren + corrigeren aanbieden. Server bepaalt het recht (admin/medewerker). */
  corrigeerbaar?: boolean;
}

type Pending = {
  actie: 'verwijderen' | 'verplaatsen';
  mediaIds: string[];
  doelFase?: string;
};

function formatTakenAt(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '';
  }
}

function TrashIcon({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}

export function MediaGrid({ wiSfId, woSfId, hideWhenEmpty = false, cardTitle, corrigeerbaar = true }: Props) {
  const [media, setMedia] = useState<MediaItem[] | null>(null);
  const [lightboxStart, setLightboxStart] = useState<number | null>(null);
  const [selectie, setSelectie] = useState<Set<string>>(new Set());
  const [selecteren, setSelecteren] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState<{ ok: boolean; tekst: string } | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [reden, setReden] = useState('');

  const laden = useCallback(() => {
    // Oplopend op taken_at, net als de klant-timeline — zo lopen de
    // fotonummers gewoon van links naar rechts.
    return fetchMedia({ wiSfId, woSfId }).then((items) =>
      [...items].sort((a, b) => a.takenAt.localeCompare(b.takenAt)),
    );
  }, [wiSfId, woSfId]);

  useEffect(() => {
    let cancelled = false;
    setMedia(null);
    setSelectie(new Set());
    setSelecteren(false);
    setMelding(null);
    setPending(null);
    laden().then((items) => {
      if (!cancelled) setMedia(items);
    });
    return () => {
      cancelled = true;
    };
  }, [laden]);

  function toggle(id: string) {
    setSelectie((vorige) => {
      const volgende = new Set(vorige);
      if (volgende.has(id)) volgende.delete(id);
      else volgende.add(id);
      return volgende;
    });
  }

  function vraagBevestiging(actie: Pending['actie'], mediaIds: string[], doelFase?: string) {
    if (mediaIds.length === 0) return;
    setMelding(null);
    setReden('');
    setPending({ actie, mediaIds, doelFase });
  }

  async function voerUit() {
    if (!pending) return;
    if (reden.trim().length < 3) {
      setMelding({ ok: false, tekst: 'Geef een reden van minstens 3 tekens.' });
      return;
    }

    setBezig(true);
    setMelding(null);
    const uitkomst = await corrigeerMedia({
      mediaIds: pending.mediaIds,
      actie: pending.actie,
      reden: reden.trim(),
      doelFase: pending.doelFase,
    });
    setMelding({ ok: uitkomst.ok, tekst: uitkomst.melding });
    if (uitkomst.ok) {
      setSelectie(new Set());
      setSelecteren(false);
      setPending(null);
      setReden('');
      setMedia(await laden());
    }
    setBezig(false);
  }

  const collapse = hideWhenEmpty || Boolean(cardTitle);
  if (media === null) {
    return collapse ? null : <p className="text-sm text-slate-400">Media laden…</p>;
  }
  if (media.length === 0) {
    return collapse ? null : <p className="text-sm text-slate-400">Nog geen eigen media.</p>;
  }

  const nummers = klantFotoNummers(media);
  const fotos: LightboxFoto[] = media
    .filter((m) => m.type === 'foto')
    .map((m) => ({
      id: m.id,
      url: m.url,
      label: `${nummers.has(m.id) ? `Foto ${nummers.get(m.id)} · ` : ''}${MEDIA_FASE_LABELS[m.fase] ?? m.fase} · ${formatTakenAt(m.takenAt)}`,
    }));
  const fotoIndex = new Map(fotos.map((f, i) => [f.id, i]));
  const fotoCount = media.filter((m) => m.type === 'foto').length;
  const videoCount = media.filter((m) => m.type === 'video').length;
  const telling = [
    fotoCount > 0 ? `${fotoCount} foto${fotoCount === 1 ? '' : "'s"}` : null,
    videoCount > 0 ? `${videoCount} video${videoCount === 1 ? '' : "'s"}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const balk = corrigeerbaar && (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {!selecteren ? (
        <button
          type="button"
          onClick={() => {
            setSelecteren(true);
            setMelding(null);
          }}
          className="px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
        >
          Selecteren
        </button>
      ) : (
        <>
          <span className="text-slate-500">{selectie.size} geselecteerd</span>
          <select
            defaultValue=""
            disabled={bezig || selectie.size === 0}
            onChange={(e) => {
              const fase = e.target.value;
              e.target.value = '';
              if (fase) vraagBevestiging('verplaatsen', [...selectie], fase);
            }}
            className="px-2 py-1 rounded border border-slate-300 bg-white text-slate-700 disabled:opacity-50"
          >
            <option value="">Verplaatsen naar…</option>
            {Object.entries(MEDIA_FASE_LABELS).map(([waarde, label]) => (
              <option key={waarde} value={waarde}>
                {label}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={bezig || selectie.size === 0}
            onClick={() => vraagBevestiging('verwijderen', [...selectie])}
            className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            Verwijderen
          </button>
          <button
            type="button"
            disabled={bezig}
            onClick={() => {
              setSelecteren(false);
              setSelectie(new Set());
              setMelding(null);
            }}
            className="px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Annuleren
          </button>
        </>
      )}
      {melding && !pending && (
        <span className={melding.ok ? 'text-emerald-600' : 'text-red-600'}>{melding.tekst}</span>
      )}
    </div>
  );

  const grid = (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {media.map((m) => {
        const gekozen = selectie.has(m.id);
        return (
          <div key={m.id} className="relative isolate z-0 group">
            {nummers.has(m.id) && (
              <span className="absolute top-1 left-1 z-[1] inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-slate-900/70 text-[11px] font-bold text-white pointer-events-none">
                {nummers.get(m.id)}
              </span>
            )}
            {selecteren && (
              <label className="absolute top-1 right-1 z-20 flex items-center justify-center w-6 h-6 rounded bg-white/90 border border-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={gekozen}
                  onChange={() => toggle(m.id)}
                  className="w-4 h-4 accent-indigo-600"
                />
              </label>
            )}
            {corrigeerbaar && !selecteren && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  vraagBevestiging('verwijderen', [m.id]);
                }}
                aria-label={m.type === 'video' ? 'Video verwijderen' : 'Foto verwijderen'}
                title="Verwijderen"
                className="absolute top-1 right-1 z-20 flex items-center justify-center w-7 h-7 rounded-md bg-black/70 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-red-600 transition-opacity [@media(hover:none)]:opacity-100"
              >
                <TrashIcon />
              </button>
            )}
            {m.type === 'video' ? (
              <video
                src={m.url}
                controls
                preload="metadata"
                playsInline
                className={`w-full aspect-square object-cover rounded-lg border bg-slate-100 ${gekozen ? 'border-indigo-600 ring-2 ring-indigo-600' : 'border-slate-200'}`}
              />
            ) : (
              <button
                type="button"
                onClick={() => (selecteren ? toggle(m.id) : setLightboxStart(fotoIndex.get(m.id) ?? 0))}
                className={`block w-full ${selecteren ? 'cursor-pointer' : 'cursor-zoom-in'}`}
              >
                <img
                  src={m.url}
                  alt={MEDIA_FASE_LABELS[m.fase] ?? m.fase}
                  loading="lazy"
                  className={`w-full aspect-square object-cover rounded-lg border bg-slate-100 ${gekozen ? 'border-indigo-600 ring-2 ring-indigo-600' : 'border-slate-200'}`}
                />
              </button>
            )}
            <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-white pointer-events-none">
              {MEDIA_FASE_LABELS[m.fase] ?? m.fase} · {formatTakenAt(m.takenAt)}
            </span>
          </div>
        );
      })}
    </div>
  );

  const lightbox = lightboxStart !== null && fotos.length > 0 && (
    <MediaLightbox
      fotos={fotos}
      start={lightboxStart}
      onClose={() => setLightboxStart(null)}
      onDelete={
        corrigeerbaar
          ? (id) => {
              setLightboxStart(null);
              vraagBevestiging('verwijderen', [id]);
            }
          : undefined
      }
    />
  );

  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
      <span className="text-xs text-slate-500">{telling}</span>
      {balk}
    </div>
  );

  const body = (
    <>
      {toolbar}
      {grid}
      {lightbox}
      {pending && (
        <RedenDialog
          pending={pending}
          reden={reden}
          onReden={setReden}
          bezig={bezig}
          fout={!melding?.ok ? melding?.tekst ?? null : null}
          onCancel={() => {
            if (bezig) return;
            setPending(null);
            setReden('');
            setMelding(null);
          }}
          onConfirm={() => void voerUit()}
        />
      )}
    </>
  );

  if (!cardTitle) return body;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <h2 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{cardTitle}</h2>
      {body}
    </div>
  );
}

function RedenDialog({
  pending,
  reden,
  onReden,
  bezig,
  fout,
  onCancel,
  onConfirm,
}: {
  pending: Pending;
  reden: string;
  onReden: (v: string) => void;
  bezig: boolean;
  fout: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const n = pending.mediaIds.length;
  const faseLabel = pending.doelFase ? (MEDIA_FASE_LABELS[pending.doelFase] ?? pending.doelFase) : '';
  const titel =
    pending.actie === 'verwijderen'
      ? n === 1
        ? 'Media verwijderen'
        : `${n} items verwijderen`
      : n === 1
        ? `Verplaatsen naar ${faseLabel}`
        : `${n} items verplaatsen naar ${faseLabel}`;
  const uitleg =
    pending.actie === 'verwijderen'
      ? 'De klant ziet dit niet meer. Geef kort aan waarom — dat wordt gelogd.'
      : 'De foto blijft zichtbaar, maar onder een andere fase.';

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[110]" onClick={bezig ? undefined : onCancel} />
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 pointer-events-none">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="media-reden-titel"
          className="bg-white rounded-xl shadow-2xl w-full max-w-sm pointer-events-auto"
        >
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 id="media-reden-titel" className="text-base font-semibold text-slate-900">
              {titel}
            </h3>
          </div>
          <form
            className="px-5 py-4 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            <p className="text-sm text-slate-600">{uitleg}</p>
            <label className="block">
              <span className="sr-only">Reden</span>
              <textarea
                autoFocus
                value={reden}
                onChange={(e) => onReden(e.target.value)}
                rows={3}
                placeholder="Reden…"
                className="w-full text-sm border border-slate-300 rounded-md px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            {fout && <p className="text-xs text-red-600">{fout}</p>}
            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={onCancel}
                disabled={bezig}
                className="px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="submit"
                disabled={bezig || reden.trim().length < 3}
                className={
                  pending.actie === 'verwijderen'
                    ? 'px-3 py-1.5 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50'
                    : 'px-3 py-1.5 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50'
                }
              >
                {bezig ? 'Bezig…' : pending.actie === 'verwijderen' ? 'Verwijderen' : 'Verplaatsen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
